/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef, useState } from 'react'

import { CookieStorage } from '@helpers'
import { PopupConfig, PopupData } from '@models/dashboard.model'

const cookieStorage = new CookieStorage()

interface ModalActions {
	viewedOnOpen?: boolean
	viewedOnOpenTimestamp?: string
	viewedOnCloseWindow?: boolean
	viewedWithDelay?: boolean
	sessionStartTime?: string
	explicitlyClosed?: boolean
	openedBy?: 'onOpen' | 'delay' | 'onClose' | null
	delayStartTime?: string
}

interface UseModalCookieControllerProps {
	popups: PopupData[]
}

export function useModalCookieController({
	popups,
}: UseModalCookieControllerProps) {
	const [modalsOpenStatus, setModalsOpenStatus] = useState<
		Record<number, boolean>
	>({})
	const broadcastChannelRef = useRef<BroadcastChannel | null>(null)
	const beforeUnloadHandled = useRef<Record<number, boolean>>({})

	const getModalConfigKey = (modalId: number) => `modal_${modalId}_config`
	const getModalActionsKey = (modalId: number) => `modal_${modalId}_actions`

	const showModal = (modalId: number) => {
		setModalsOpenStatus((prev) => ({ ...prev, [modalId]: true }))
	}

	const hideModal = (modalId: number) => {
		setModalsOpenStatus((prev) => ({ ...prev, [modalId]: false }))
	}

	const getModalActions = useCallback((modalId: number): ModalActions => {
		const actions = cookieStorage.get(getModalActionsKey(modalId))
		const parsedActions = actions ? JSON.parse(actions) : {}

		return {
			viewedOnOpen: false,
			viewedOnCloseWindow: false,
			viewedWithDelay: false,
			explicitlyClosed: false,
			openedBy: null,
			...parsedActions,
		}
	}, [])

	const updateModalActions = useCallback(
		(modalId: number, updates: Partial<ModalActions>) => {
			const currentActions = getModalActions(modalId)
			const now = new Date().toISOString()

			if (!currentActions.openedBy) {
				const openedByMap = {
					viewedOnOpen: 'onOpen',
					viewedWithDelay: 'delay',
					viewedOnCloseWindow: 'onClose',
				}

				const trigger = Object.keys(openedByMap).find(
					(key) => updates[key as keyof typeof updates]
				) as keyof typeof openedByMap | undefined

				if (trigger) {
					updates.openedBy = openedByMap[trigger] as
						| 'onOpen'
						| 'delay'
						| 'onClose'
						| null
				}
			}

			if (!currentActions.sessionStartTime) {
				updates.sessionStartTime = now
			}

			const filteredUpdates = Object.fromEntries(
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				Object.entries(updates).filter(([_, value]) => value !== undefined)
			)

			const newActions = {
				...currentActions,
				...filteredUpdates,
			}

			cookieStorage.set({
				key: getModalActionsKey(modalId),
				value: JSON.stringify(newActions),
			})

			if (broadcastChannelRef.current) {
				broadcastChannelRef.current.postMessage({
					type: 'modal_actions_updated',
					modalId,
					actions: newActions,
				})
			}

			return newActions
		},
		[getModalActions]
	)

	const setupBeforeUnloadListener = useCallback(
		(modalId: number, config: PopupConfig) => {
			if (!config.onClose || beforeUnloadHandled.current[modalId]) return

			const handleBeforeUnload = (e: BeforeUnloadEvent) => {
				const actions = getModalActions(modalId)

				if (config.onClose && !actions.viewedOnCloseWindow) {
					showModal(modalId)
					updateModalActions(modalId, {
						viewedOnCloseWindow: true,
						openedBy: 'onClose',
					})

					e.preventDefault()
					e.returnValue = ''

					return e.returnValue
				}

				return null
			}

			window.addEventListener('beforeunload', handleBeforeUnload)
			beforeUnloadHandled.current[modalId] = true

			// eslint-disable-next-line consistent-return
			return () => {
				window.removeEventListener('beforeunload', handleBeforeUnload)
				beforeUnloadHandled.current[modalId] = false
			}
		},
		[getModalActions, updateModalActions]
	)

	const calculateStartTime = (allPopups: PopupData[]) => {
		const closedOnOpenModals = allPopups.filter((popup) => {
			const popupActions = getModalActions(popup.id)

			return popup.onOpen && popupActions.viewedOnOpenTimestamp
		})

		if (closedOnOpenModals.length > 0) {
			const lastClosedModal = closedOnOpenModals.reduce((latest, current) => {
				const latestTime = new Date(
					getModalActions(latest.id).viewedOnOpenTimestamp || ''
				).getTime()
				const currentTime = new Date(
					getModalActions(current.id).viewedOnOpenTimestamp || ''
				).getTime()

				return currentTime > latestTime ? current : latest
			})

			return new Date(
				getModalActions(lastClosedModal.id).viewedOnOpenTimestamp || ''
			).getTime()
		}

		const hasUnclosedOnOpenModals = allPopups.some((popup) => {
			const popupActions = getModalActions(popup.id)

			return popup.onOpen && !popupActions.explicitlyClosed
		})

		if (hasUnclosedOnOpenModals) return Date.now()

		const sessionStart = allPopups[0]
			? getModalActions(allPopups[0].id).sessionStartTime
			: null

		return sessionStart ? new Date(sessionStart).getTime() : Date.now()
	}

	const checkDelay = useCallback(
		(modalId: number, config: PopupConfig, allPopups: PopupData[]) => {
			if (!config.delay) return

			const actions = getModalActions(modalId)

			if (actions.viewedWithDelay || actions.viewedOnCloseWindow) return

			const delayStartTime = calculateStartTime(allPopups)
			const delayMs = config.delay * 60 * 1000
			const triggerTime = delayStartTime + delayMs

			if (
				!actions.delayStartTime ||
				new Date(actions.delayStartTime).getTime() !== delayStartTime
			) {
				updateModalActions(modalId, {
					delayStartTime: new Date(delayStartTime).toISOString(),
				})
			}

			if (Date.now() >= triggerTime) {
				showModal(modalId)
				updateModalActions(modalId, {
					viewedWithDelay: true,
					openedBy: 'delay',
				})
			} else {
				const timer = setTimeout(() => {
					showModal(modalId)
					updateModalActions(modalId, {
						viewedWithDelay: true,
						openedBy: 'delay',
					})
				}, triggerTime - Date.now())

				// eslint-disable-next-line consistent-return
				return () => clearTimeout(timer)
			}
		},
		[getModalActions, updateModalActions]
	)

	useEffect(() => {
		if (!broadcastChannelRef.current) {
			broadcastChannelRef.current = new BroadcastChannel('global_modal_channel')
		}
		const channel = broadcastChannelRef.current

		const cleanupFunctions: (() => void)[] = []

		popups.forEach((popup) => {
			const modalId = popup.id
			const config: PopupConfig = {
				onOpen: popup.onOpen,
				onClose: popup.onClose,
				delay: popup.delay,
			}

			cookieStorage.set({
				key: getModalConfigKey(modalId),
				value: JSON.stringify(config),
			})

			const actions = getModalActions(modalId)

			if (!actions.sessionStartTime) {
				updateModalActions(modalId, {
					sessionStartTime: new Date().toISOString(),
				})
			}

			if (config.onOpen && !actions.explicitlyClosed) {
				showModal(modalId)
				updateModalActions(modalId, {
					viewedOnOpen: true,
					openedBy: 'onOpen',
				})
			} else {
				hideModal(modalId)
			}

			const cleanupBeforeUnload = setupBeforeUnloadListener(modalId, config)

			if (cleanupBeforeUnload) cleanupFunctions.push(cleanupBeforeUnload)

			const cleanupDelay = checkDelay(modalId, config, popups)

			if (cleanupDelay) cleanupFunctions.push(cleanupDelay)
		})

		const handleMessage = (event: MessageEvent) => {
			if (event.data.type === 'modal_actions_updated') {
				const { modalId: updatedModalId, actions: updatedActions } = event.data

				setModalsOpenStatus((prev) => {
					const newState = { ...prev }
					const currentPopupConfig = popups.find(
						(popup) => popup.id === updatedModalId
					)

					if (updatedActions.explicitlyClosed) {
						newState[updatedModalId] = false
					} else if (currentPopupConfig?.onOpen) {
						newState[updatedModalId] = !updatedActions.viewedOnOpen
					}

					return newState
				})

				const updatedPopup = popups.find((popup) => popup.id === updatedModalId)

				if (
					updatedPopup &&
					updatedPopup.delay &&
					!updatedActions.viewedWithDelay
				) {
					checkDelay(updatedModalId, updatedPopup, popups)
				}
			}
		}

		channel.addEventListener('message', handleMessage)
		cleanupFunctions.push(() =>
			channel.removeEventListener('message', handleMessage)
		)

		return () => {
			cleanupFunctions.forEach((cleanup) => cleanup())
		}
	}, [
		popups,
		getModalActions,
		updateModalActions,
		setupBeforeUnloadListener,
		checkDelay,
	])

	const updateAllModalsDelayStartTime = useCallback(
		(timestamp: string) => {
			popups.forEach((popup) => {
				const modalId = popup.id

				if (popup.delay) {
					updateModalActions(modalId, {
						delayStartTime: timestamp,
					})
				}
			})
		},
		[popups, getModalActions, updateModalActions]
	)

	const forceCloseModal = useCallback(
		(modalId: number) => {
			hideModal(modalId)
			const currentActions = getModalActions(modalId)
			const now = new Date().toISOString()

			const baseUpdate = {
				explicitlyClosed: true,
			}

			switch (currentActions.openedBy) {
				case 'onOpen':
					updateModalActions(modalId, {
						...baseUpdate,
						viewedOnOpen: true,
						viewedOnOpenTimestamp: now,
					})
					updateAllModalsDelayStartTime(now)
					break

				case 'delay':
					updateModalActions(modalId, {
						...baseUpdate,
						viewedWithDelay: true,
					})
					break

				case 'onClose':
					updateModalActions(modalId, {
						...baseUpdate,
						viewedOnCloseWindow: true,
					})
					break

				default:
					updateModalActions(modalId, baseUpdate)
			}
		},
		[updateModalActions, getModalActions, updateAllModalsDelayStartTime]
	)

	const getModalTrigger = useCallback(
		(modalId: number): 'onOpen' | 'delay' | 'onClose' | null => {
			const actions = getModalActions(modalId)

			return actions.openedBy || null
		},
		[getModalActions]
	)

	return {
		modalsOpenStatus,
		updateModalActions,
		getModalActions,
		forceCloseModal,
		getModalTrigger,
	}
}
